import React, { useEffect, useMemo, useState } from 'react';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import useTranslate from '../../../../../customHooks/useTranslate';
import Title from '../../../../components/Title';
import { ZinantisMemberDescriptionState } from '../../../../../types/zinantis/members/membersType';
import useLanguage from '../../../../../customHooks/useLanguage';
import { sanitizeHTMLforDescriptions } from '../../../../../utils/sanitizeInjectedHTML';

interface DescriptionSectionProps {
    descriptions: ZinantisMemberDescriptionState[];
}

const DescriptionSection = (props: DescriptionSectionProps) => {
    const { descriptions = [] } = props;
    const translate = useTranslate();
    const appLanguage = useLanguage();

    const [visibleDescription, setVisibleDescription] =
        useState<ZinantisMemberDescriptionState | null>(null);

    const descriptionSelectOptions = useMemo(() => {
        return descriptions.map(desc => ({
            id: desc.id,
            label: desc.language,
        }));
    }, [descriptions]);

    const handleSelectedDescription = (e: SelectChangeEvent) => {
        const idValue = e.target.value;
        const newDescription =
            descriptions.find(desc => desc.id === idValue?.toString()) || null;
        setVisibleDescription(newDescription);
    };

    const getInitialDescription = () => {
        if (!descriptions?.length) return null;
        const descriptionInAppLanguage = descriptions.find(
            desc => desc.code === appLanguage
        );
        const descriptionInDefaultLanguage = descriptions.find(
            desc => desc.code === 'en'
        );

        return (
            descriptionInAppLanguage ||
            descriptionInDefaultLanguage ||
            descriptions[0]
        );
    };

    useEffect(() => {
        setVisibleDescription(getInitialDescription());
    }, []);

    // para que envuelva en <p> si es texto plano
    const wrapPlainTextInParagraph = (text: string) => {
        if (!text) return '';
        // Detecta si contiene alguna etiqueta HTML
        const hasHTML = /<\/?[a-z][\s\S]*>/i.test(text);
        if (hasHTML) {
            // Si ya contiene HTML, devolver tal cual
            return text;
        }
        // Escapar caracteres especiales
        const escapeHtml = (str: string) =>
            str
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

        return `<p>${escapeHtml(text)}</p>`;
    };

    return (
        <>
            {visibleDescription ? (
                <div className='flex-column gap-5'>
                    <div className='flex flex-align-end flex-between'>
                        <Title>{translate('description2')}</Title>
                        <Select
                            size='small'
                            value={visibleDescription.id}
                            onChange={handleSelectedDescription}
                        >
                            {descriptionSelectOptions.map(desc => (
                                <MenuItem key={desc.id} value={desc.id}>
                                    {desc.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHTMLforDescriptions(
                                wrapPlainTextInParagraph(
                                    visibleDescription.description
                                )
                            ),
                        }}
                    />
                </div>
            ) : null}
        </>
    );
};

export default DescriptionSection;
